import { useState, useEffect } from 'react';
import { Bell, Shield, Sun, Download, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { MobileTopbar } from '@/mobile/shared/MobileTopbar';
import { MeRow } from './MeRow';

/** Iniciais do nome do usuário */
function initials(name: string | undefined): string {
  if (!name) return 'GK';
  const parts = name.split(' ').filter(Boolean).slice(0, 2);
  return parts.map((s) => (s[0] ?? '').toUpperCase()).join('');
}

/** Toggle simples reusável */
function Toggle({
  on,
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      style={{
        width: 44,
        height: 26,
        borderRadius: 'var(--r-pill)',
        background: on ? 'var(--mint-deep)' : 'var(--shadow-pressed, var(--card-soft))',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        transition: 'background 0.2s ease',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 21 : 3,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.2s ease',
        }}
        aria-hidden
      />
    </button>
  );
}

/** Seletor de tema: light / dark / system */
function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const options: Array<{ value: typeof theme; label: string }> = [
    { value: 'light', label: 'Claro' },
    { value: 'dark', label: 'Escuro' },
    { value: 'system', label: 'Sistema' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        background: 'var(--card-soft)',
        borderRadius: 'var(--r-pill)',
        padding: 3,
      }}
      role="group"
      aria-label="Tema do aplicativo"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => setTheme(opt.value)}
          aria-pressed={theme === opt.value}
          style={{
            padding: '4px 10px',
            borderRadius: 'var(--r-pill)',
            fontSize: 11,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            background: theme === opt.value ? 'var(--frame)' : 'transparent',
            color: theme === opt.value ? 'var(--fg)' : 'var(--fg-muted)',
            boxShadow: theme === opt.value ? 'var(--shadow-1)' : 'none',
            transition: 'background 0.15s ease',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Hook para verificar e solicitar push notifications */
function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    setSupported(isSupported);
    if (isSupported) {
      setEnabled(Notification.permission === 'granted');
    }
  }, []);

  async function toggle(value: boolean) {
    if (!supported) return;
    if (value) {
      try {
        const permission = await Notification.requestPermission();
        setEnabled(permission === 'granted');
        if (permission !== 'granted') {
          toast.error('Permissão de notificações negada. Habilite nas configurações do seu dispositivo.');
        } else {
          toast.success('Notificações push ativadas.');
        }
      } catch {
        toast.error('Não foi possível ativar as notificações agora.');
      }
    } else {
      setEnabled(false);
      toast('Notificações desativadas. Para reativar, atualize nas configurações do dispositivo.');
    }
  }

  return { supported, enabled, toggle };
}

/** Hook para PWA install prompt */
function usePWAInstall() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [prompt, setPrompt] = useState<any>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (e: any) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function install() {
    if (!prompt) return;
    await prompt.prompt();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { outcome } = await prompt.userChoice as { outcome: string };
    if (outcome === 'accepted') {
      toast.success('App instalado com sucesso!');
      setPrompt(null);
    }
  }

  return { canInstall: !!prompt, install };
}

/** Versão do app — lida de VITE_APP_VERSION ou fallback */
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? '0.0.0';

/**
 * Módulo Eu — perfil + configurações do usuário.
 * Sair faz signOut + queryClient.clear() via ConfirmDialog (sem confirm nativo).
 */
export function EuModule() {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const push = usePushNotifications();
  const pwa = usePWAInstall();

  const userInitials = initials(user?.name);

  async function handleSignOut() {
    setLoggingOut(true);
    try {
      await logout();
      queryClient.clear();
      navigate({ to: '/login' });
    } catch {
      toast.error('Falha ao sair. Tente novamente.');
    } finally {
      setLoggingOut(false);
      setConfirmSignOut(false);
    }
  }

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        minHeight: 0,
        padding: '8px 18px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <MobileTopbar title="Eu" subtitle="Conta & preferências" />

      {/* Card perfil */}
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        {/* Avatar 72px circular gradient clay */}
        <span
          aria-hidden
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--clay-deep), #8E5736)',
            color: 'var(--clay-soft, #FBF7EF)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {userInitials}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: 'var(--fg)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name ?? 'Usuário'}
          </div>
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.email ?? '—'}
          </div>
          {user?.workspaceName && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--mint-deep)',
                marginTop: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.workspaceName}
            </div>
          )}
        </div>
      </div>

      {/* Preferências */}
      <div
        role="list"
        aria-label="Preferências"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: '0 16px',
        }}
      >
        {/* Notificações push */}
        <MeRow
          icon={Bell}
          title="Notificações push"
          sub={push.supported ? 'prazos e automação' : 'não disponível neste dispositivo'}
          disabled={!push.supported}
          aria-label="Notificações push"
        >
          <Toggle
            on={push.enabled}
            onChange={push.toggle}
            disabled={!push.supported}
            aria-label="Ativar ou desativar notificações push"
          />
        </MeRow>

        {/* Tema */}
        <MeRow
          icon={Sun}
          title="Tema"
          sub="aparência do app"
        >
          <ThemeSelector />
        </MeRow>

        {/* MFA */}
        <MeRow
          icon={Shield}
          title="Autenticação de 2 fatores"
          sub="MFA · configurações avançadas"
          onClick={() => {
            /* TODO: navegar para /configuracoes via deep link desktop */
            toast('Para gerenciar o MFA, acesse as configurações no computador.');
          }}
        />

        {/* Instalar app — só exibe se beforeinstallprompt disponível */}
        {pwa.canInstall && (
          <MeRow
            icon={Download}
            title="Instalar app"
            sub="adicionar à tela inicial"
            onClick={pwa.install}
            aria-label="Instalar o GestAKI como app"
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                color: 'var(--mint-deep)',
              }}
            >
              Instalar
            </span>
          </MeRow>
        )}
      </div>

      {/* Sair */}
      <div
        role="list"
        aria-label="Sessão"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: '0 16px',
        }}
      >
        <MeRow
          icon={LogOut}
          title="Sair"
          danger
          onClick={() => setConfirmSignOut(true)}
          aria-label="Encerrar sessão"
        >
          <span aria-hidden />
        </MeRow>
      </div>

      {/* Versão do app */}
      <div
        style={{
          textAlign: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          color: 'var(--fg-dim)',
          marginTop: 8,
        }}
      >
        GestAKI v{APP_VERSION}
      </div>

      {/* ConfirmDialog sair */}
      <ConfirmDialog
        open={confirmSignOut}
        onOpenChange={setConfirmSignOut}
        title="Encerrar sessão?"
        description="Você será desconectado do GestAKI neste dispositivo."
        confirmLabel="Sair"
        cancelLabel="Cancelar"
        loading={loggingOut}
        onConfirm={handleSignOut}
      />
    </div>
  );
}
